import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  IconButton,
  Collapse,
  FormControlLabel,
  Switch,
  useTheme,
  alpha,
  Paper,
  Popper,
} from '@mui/material';
import {
  Person,
  Business,
  Search,
  FilterList,
  ExpandMore,
  ExpandLess,
  Circle,
  Refresh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AgentUser } from '../types';
import { userService } from '../services/user';

export interface Company {
  id: string;
  name: string;
  domain?: string;
  userCount?: number;
}

interface UserSelectorProps {
  value?: AgentUser | null;
  onChange: (user: AgentUser | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  showStatus?: boolean;
  showCompanyFilter?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
}

const UserSelector: React.FC<UserSelectorProps> = ({
  value = null,
  onChange,
  label = 'Select User',
  placeholder = 'Type to search users...',
  required = false,
  error = false,
  helperText,
  disabled = false,
  showStatus = true,
  showCompanyFilter = true,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<AgentUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(true); // Default to online only

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const userList = await userService.getUserList();
      // Since backend only returns online users, mark them all as online
      const usersWithOnlineStatus = userList.map(user => ({
        ...user,
        isOnline: true
      }));
      setUsers(usersWithOnlineStatus);
      
      // Extract unique companies from users
      const companyMap = new Map<string, Company>();
      usersWithOnlineStatus.forEach(user => {
        if (user.agentName && !companyMap.has(user.agentName)) {
          companyMap.set(user.agentName, {
            id: user.agentId,
            name: user.agentName,
            userCount: 1,
          });
        } else if (user.agentName && companyMap.has(user.agentName)) {
          const company = companyMap.get(user.agentName)!;
          company.userCount = (company.userCount || 0) + 1;
        }
      });
      setCompanies(Array.from(companyMap.values()));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search, company, and online status
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by company
    if (selectedCompany) {
      filtered = filtered.filter(u => u.agentName === selectedCompany.name);
    }

    // Filter by online status
    if (onlineOnly) {
      filtered = filtered.filter(u => u.isOnline);
    }

    // Filter by search query
    if (inputValue) {
      const query = inputValue.toLowerCase();
      filtered = filtered.filter(u =>
        u.fullName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        u.agentName.toLowerCase().includes(query) ||
        u.userName.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [users, selectedCompany, onlineOnly, inputValue]);

  const CustomPopper = (props: any) => (
    <Popper {...props} placement="bottom-start" style={{ zIndex: 1301 }}>
      <Paper
        sx={{
          mt: 1,
          boxShadow: theme.shadows[8],
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        }}
      >
        {props.children}
      </Paper>
    </Popper>
  );

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {showCompanyFilter && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              mb: 1,
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterList sx={{ fontSize: 20 }} />
            <Typography variant="body2" fontWeight={500}>
              Advanced Filters
            </Typography>
            {showFilters ? <ExpandLess /> : <ExpandMore />}
          </Box>

          <Collapse in={showFilters}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ 
                p: 2, 
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}>
                <Autocomplete
                  value={selectedCompany}
                  onChange={(_, newValue) => setSelectedCompany(newValue)}
                  options={companies}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Company"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Business />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                          <Business sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.userCount} users
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={onlineOnly}
                        onChange={(e) => setOnlineOnly(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Online users only"
                  />
                  <IconButton size="small" onClick={loadUsers} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </Box>
              </Box>
            </motion.div>
          </Collapse>
        </Box>
      )}

      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        options={filteredUsers}
        loading={loading}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        PopperComponent={CustomPopper}
        getOptionLabel={(option) => option.fullUserLabel || `${option.fullName} (${option.email})`}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            variant={variant}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props as any;
          return (
            <Box component="li" key={key} {...otherProps}>
            <ListItemAvatar>
              <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main }}>
                {option.initials || option.fullName[0]}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {option.fullName}
                  </Typography>
                  {showStatus && option.isOnline && (
                    <Circle 
                      sx={{ 
                        fontSize: 8,
                        color: theme.palette.success.main,
                      }} 
                    />
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography variant="caption" component="span">
                    {option.email}
                  </Typography>
                  <Box component="span" sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    {option.agentName && (
                      <Chip
                        label={option.agentName}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.7rem',
                          background: alpha(theme.palette.primary.main, 0.1),
                        }}
                      />
                    )}
                    {option.userName && (
                      <Chip
                        label={`@${option.userName}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.7rem',
                          background: alpha(theme.palette.secondary.main, 0.1),
                        }}
                      />
                    )}
                  </Box>
                </>
              }
            />
          </Box>
          );
        }}
      />
    </Box>
  );
};

export default UserSelector;